import * as React from "react"
import {
  BillingProductName,
  useCheckBillingOrganizationPlanQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgAnnouncement04 } from "@digits-shared/components/SVGIcons/line/Announcement04.svg"
import { SvgCreditCardShield } from "@digits-shared/components/SVGIcons/line/CreditCardShield.svg"
import { SvgFileShield02 } from "@digits-shared/components/SVGIcons/line/FileShield02.svg"
import { SvgLogOut01 } from "@digits-shared/components/SVGIcons/line/LogOut01.svg"
import { ColorIcon } from "@digits-shared/components/UI/Elements/ColorIcon"
import { isOrgNonSandboxAccountingFirm } from "@digits-shared/helpers/organizationHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useSession from "@digits-shared/hooks/useSession"
import { JWTPermissionFlag } from "@digits-shared/session/jwt/jwtPermissions"
import colors from "@digits-shared/themes/colors"
import styled, { css } from "styled-components"
import { isBillablePlan } from "src/frontend/components/OS/Applications/Settings/hooks/useHasBillablePlan"
import { useLogOut } from "src/frontend/components/Session/useLogOut"
import { SidebarViewType } from "src/frontend/components/Shared/NavSidebar/Menu/SidebarViewType"
import {
  SidebarAnchorRow,
  SidebarRow,
} from "src/frontend/components/Shared/NavSidebar/SidebarExpando"
import { useHasPermission } from "src/frontend/components/Shared/Permissions/Requires"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import {
  FrontendPermissionModule,
  FrontendPermissionSource,
} from "src/frontend/session/permissionModule"

/*
  STYLES
*/

const SVG_STYLES = css`
  height: 20px;
  width: 20px;
  display: block;
  margin: 0 auto;
  ${svgPathStyles(colors.secondary, 1.7)};
`

const MenuSection = styled.div`
  padding: 6px 0;
  width: 100%;
  border-top: 1px solid ${colors.translucentSecondary10};

  &.share {
    padding-top: 12px;
  }
`

const MenuRow = styled(SidebarRow)`
  width: -webkit-fill-available;
  svg {
    ${SVG_STYLES}
  }
`

const LogoutRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  svg {
    ${SVG_STYLES};
  }
`

const FeedbackIcon = styled(SvgAnnouncement04)`
  ${SVG_STYLES};
`

/*
 INTERFACES
*/

interface Props {
  onItemClick?: () => void
  isShareUser?: boolean
}

export const MenuOtherLinks: React.FC<Props> = ({ onItemClick, isShareUser }) => (
  <>
    <MenuSection className={stringHelper.classNames(isShareUser && "share")}>
      <MenuRow Icon={SvgFileShield02} route={routes.sharedWithMe} onClick={onItemClick}>
        Shared with me
      </MenuRow>
      <BillingRow onItemClick={onItemClick} />
      <SendFeedbackLink onItemClick={onItemClick} />
      <SidebarViewType onItemClick={onItemClick} />
    </MenuSection>

    <MenuSection>
      <LogOut />
    </MenuSection>
  </>
)

const LogOut: React.FC = () => {
  const [isLoggingOut, startTransition] = React.useTransition()
  const session = useSession<FrontendSession>()
  const logOut = useLogOut()

  const onLogOut = React.useCallback(() => {
    startTransition(() => logOut().then())
  }, [logOut])

  return (
    <MenuRow
      Icon={() => (
        <ColorIcon
          css={{ marginLeft: "-1px" }}
          imageUrl={session.user.avatarUrl}
          fallbackUser={session.user}
          size={24}
          fontSize={12}
        />
      )}
      onClick={onLogOut}
    >
      <LogoutRow>
        <div css="flex: 1">{isLoggingOut ? "Signing Out…" : "Sign Out"}</div>
        <SvgLogOut01 />
      </LogoutRow>
    </MenuRow>
  )
}

const BillingRow: React.FC<Props> = ({ onItemClick }) => {
  const { currentOrganization } = useSession<FrontendSession>()
  const orgSlug = currentOrganization?.slug ?? ""
  const canRead = useHasPermission({
    source: FrontendPermissionSource.Organization,
    module: FrontendPermissionModule.Billing,
    flag: JWTPermissionFlag.Read,
  })
  const canUpdate = useHasPermission({
    source: FrontendPermissionSource.Organization,
    module: FrontendPermissionModule.Billing,
    flag: JWTPermissionFlag.Update,
  })

  // We are using CheckBillingOrganizationPlan here to avoid an undue call to ReadBillingOrganizationPlan,
  // as Check is used in other places to indicate whether the Add Client route should go to the legacy or new onboarding flow
  const { data } = useCheckBillingOrganizationPlanQuery({
    variables: {
      organizationId: currentOrganization?.id || "",
    },
    skip:
      !currentOrganization?.id ||
      !canRead ||
      !canUpdate ||
      !isOrgNonSandboxAccountingFirm(currentOrganization),
  })

  const planName =
    data?.checkBillingOrganizationPlan.billingPlan?.product.name || BillingProductName.UnknownName

  return (
    isBillablePlan(planName) && (
      <MenuRow
        Icon={SvgCreditCardShield}
        route={routes.organizationBilling}
        params={{ orgSlug }}
        onClick={onItemClick}
      >
        Billing
      </MenuRow>
    )
  )
}

const SendFeedbackLink: React.FC<Props> = ({ onItemClick }) => (
  <SidebarAnchorRow
    css="width: -webkit-fill-available;"
    Icon={FeedbackIcon}
    href="https://help.digits.com/support/tickets/new"
    target="_blank"
    rel="noopener noreferrer"
    onClick={onItemClick}
  >
    Send Feedback
  </SidebarAnchorRow>
)
